// src/components/ProjectWebsite.jsx
// Reiter "Website" der Projektseite: Inhalte für die EXTERNE Projektwebsite pflegen
// (US-WEBP-001). Der Reiter erscheint nur, wenn die GL für dieses Projekt den
// Schalter "Externe Projektwebsite" gesetzt hat (Projekt-Property Projektwebsite).
//
// Bearbeitungsrecht: gemeinsame Projekt-Zuordnung (Tabelle
// projectEmployeeAssignments) wie Controlling/Pendenzen/Kostenschätzung — GL/PL
// immer, sonst nur dem Projekt zugeteilte Team-Mitglieder (ADR 0020).
//
// Bausteine werden einzeln aktiviert; inaktive Bausteine sind ausgegraut und nicht
// ausfüllbar. Terminplan, Ferienkalender, Aktueller Plansatz und Bilder sind
// bewusst reine Platzhalter (aktivierbar, noch ohne Inhalte).
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  WEBSITE_BLOCKS,
  getProjectWebsite,
  saveProjectWebsite,
  setWebsitePassword,
  removeWebsitePassword,
  suggestWebsitePassword,
  createWebsitePlan,
  addWebsitePlanIndex,
  updateWebsitePlanMeta,
  setWebsitePlanCurrentVersion,
  updateWebsitePlanOrder,
  deleteWebsitePlan,
  emptyWebsiteData,
  emptyAdresse,
  PASSWORD_MIN_LENGTH,
} from "../services/azureProjectWebsiteService";
import { getProjectAssignments } from "../services/azureProjectAssignmentService";
import { canEditProjectAssigned } from "../utils/permissions";
import { useAuth } from "../hooks/useAuth";
import PendenzMembersModal from "./PendenzMembersModal";
import ProjectWebsitePlanstand from "./ProjectWebsitePlanstand";
import "../styles/project-website.scss";

// Slug wie in ProjectPageWrapper (Code + Titel) — wird als ProjectSlug in der
// config-Zeile mitgespeichert, damit der Microservice die Website ohne Umweg über
// die interne Route adressieren kann.
const buildSlug = (project) => {
  const titleSlug =
    project?.title
      ?.toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "") || "";
  return `${project?.code || ""}-${titleSlug}`;
};

const ADRESS_FELDER = [
  { key: "firma", label: "Firma" },
  { key: "person", label: "Person" },
  { key: "telefon", label: "Telefonnummer", type: "tel" },
  { key: "email", label: "Email", type: "email" },
  { key: "adresse", label: "Adresse" },
  { key: "ferienvertretung", label: "Ferienvertretung" },
];

const isEmptyAdresse = (a) =>
  ADRESS_FELDER.every((f) => !String(a[f.key] || "").trim());

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const ProjectWebsite = ({ project, readOnly = false }) => {
  const { user } = useAuth();
  const projectUuid = project?.id;
  const projectCode = project?.code;

  const [assignments, setAssignments] = useState([]);
  const [data, setData] = useState(emptyWebsiteData);
  const [deletedAdressIds, setDeletedAdressIds] = useState([]);
  const [persistedAdressIds, setPersistedAdressIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [showMembers, setShowMembers] = useState(false);

  // Zugangsschutz: Klartext bleibt im lokalen State und wird nur zum Ableiten des
  // Hashes verwendet — gespeichert wird ausschliesslich der Hash.
  const [pwNeu, setPwNeu] = useState("");
  const [pwWdh, setPwWdh] = useState("");
  const [pwSichtbar, setPwSichtbar] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState(null);

  // Schreibrecht: Freelancer:innen (readOnly-Prop) nie; sonst GL/PL immer und
  // Team-Mitglieder nur bei Projekt-Zuordnung.
  const canWrite = !readOnly && canEditProjectAssigned(user, assignments);

  const applyLoaded = useCallback((loaded) => {
    setData(loaded);
    setPersistedAdressIds(loaded.adressen.map((a) => a.id));
    setDeletedAdressIds([]);
    setDirty(false);
  }, []);

  const loadAssignments = useCallback(async () => {
    if (!projectCode) return;
    try {
      setAssignments(await getProjectAssignments(projectCode));
    } catch {
      /* nicht kritisch – ohne Zuordnungen bleibt es bei GL/PL */
    }
  }, [projectCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectUuid) return;
      try {
        setLoading(true);
        setError(null);
        const [loaded] = await Promise.all([
          getProjectWebsite(projectUuid),
          loadAssignments(),
        ]);
        if (cancelled) return;
        applyLoaded(loaded);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Laden fehlgeschlagen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectUuid, loadAssignments, applyLoaded]);

  const markDirty = () => {
    setDirty(true);
    setInfo(null);
  };

  const toggleBlock = (key) => {
    if (!canWrite) return;
    setData((prev) => ({
      ...prev,
      blocks: { ...prev.blocks, [key]: !prev.blocks[key] },
    }));
    markDirty();
  };

  const setPlanstandField = (field, value) => {
    setData((prev) => ({ ...prev, planstand: { ...prev.planstand, [field]: value } }));
    markDirty();
  };

  const setAdressFeld = (id, field, value) => {
    setData((prev) => ({
      ...prev,
      adressen: prev.adressen.map((a) => (a.id === id ? { ...a, [field]: value } : a)),
    }));
    markDirty();
  };

  const addAdresse = () => {
    setData((prev) => ({ ...prev, adressen: [...prev.adressen, emptyAdresse()] }));
    markDirty();
  };

  const removeAdresse = (id) => {
    setData((prev) => ({ ...prev, adressen: prev.adressen.filter((a) => a.id !== id) }));
    if (persistedAdressIds.includes(id)) {
      setDeletedAdressIds((prev) => [...prev, id]);
    }
    markDirty();
  };

  const handleSave = async () => {
    if (!canWrite || saving) return;
    try {
      setSaving(true);
      setError(null);
      setInfo(null);
      // Vollständig leere Zeilen werden nicht geschrieben; waren sie bereits
      // gespeichert, werden sie entfernt (sonst bliebe eine leere Zeile in Azure).
      const kept = data.adressen.filter((a) => !isEmptyAdresse(a));
      const emptied = data.adressen
        .filter((a) => isEmptyAdresse(a) && persistedAdressIds.includes(a.id))
        .map((a) => a.id);
      const saved = await saveProjectWebsite(
        projectUuid,
        {
          projectCode,
          projectSlug: buildSlug(project),
          blocks: data.blocks,
          planstand: data.planstand,
          adressen: kept,
          deletedAdressIds: [...deletedAdressIds, ...emptied],
        },
        user?.email || user?.userDetails || ""
      );
      applyLoaded(saved);
      setInfo("Änderungen gespeichert.");
    } catch (err) {
      setError(err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  // Planstand-Register: die Service-Operationen schreiben sofort und liefern den
  // frischen Gesamtstand zurück. Übernommen wird bewusst NUR `plaene` — ein
  // applyLoaded() würde hier unsichtbar die noch nicht gespeicherten Eingaben in
  // Kopfangaben/Adressliste überschreiben und `dirty` zurücksetzen.
  const uebernehmePlaene = (fresh) =>
    setData((prev) => ({ ...prev, plaene: fresh.plaene }));

  const alsWer = () => user?.email || user?.userDetails || "";

  const handlePlanCreate = async (form) => {
    const fresh = await createWebsitePlan(
      projectUuid,
      { ...form, sortOrder: data.plaene.length },
      { projectCode, projectSlug: buildSlug(project) },
      alsWer()
    );
    uebernehmePlaene(fresh);
  };

  const handlePlanAddIndex = async (planId, form) => {
    uebernehmePlaene(
      await addWebsitePlanIndex(projectUuid, planId, form, alsWer())
    );
  };

  const handlePlanUpdateMeta = async (planId, form) => {
    uebernehmePlaene(
      await updateWebsitePlanMeta(projectUuid, planId, form, alsWer())
    );
  };

  const handlePlanSetCurrent = async (planId, versionId) => {
    uebernehmePlaene(
      await setWebsitePlanCurrentVersion(projectUuid, planId, versionId)
    );
  };

  const handlePlanDelete = async (planId) => {
    uebernehmePlaene(await deleteWebsitePlan(projectUuid, planId));
  };

  // Reihenfolge und Gruppen kommen als eine fertige Liste [{ id, gruppe }] in
  // Anzeigereihenfolge — Verschieben, Gruppenwechsel und Umbenennen laufen
  // bewusst über denselben Weg (siehe ProjectWebsitePlanstand).
  const handlePlanReorder = async (ordnung) => {
    uebernehmePlaene(await updateWebsitePlanOrder(projectUuid, ordnung, alsWer()));
  };

  const resetPwFelder = () => {
    setPwNeu("");
    setPwWdh("");
    setPwSichtbar(false);
    setPwError(null);
  };

  const handleSuggestPassword = () => {
    const vorschlag = suggestWebsitePassword();
    setPwNeu(vorschlag);
    setPwWdh(vorschlag);
    setPwSichtbar(true);
    setPwError(null);
  };

  const handleSetPassword = async () => {
    if (!canWrite || pwBusy) return;
    if (pwNeu.length < PASSWORD_MIN_LENGTH) {
      setPwError(`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen nötig.`);
      return;
    }
    if (pwNeu !== pwWdh) {
      setPwError("Die beiden Eingaben stimmen nicht überein.");
      return;
    }
    try {
      setPwBusy(true);
      setPwError(null);
      setInfo(null);
      const state = await setWebsitePassword(
        projectUuid,
        pwNeu,
        { projectCode, projectSlug: buildSlug(project) },
        user?.email || user?.userDetails || ""
      );
      setData((prev) => ({ ...prev, password: state }));
      resetPwFelder();
      setInfo(
        "Zugangspasswort gespeichert. Es lässt sich später nicht mehr anzeigen — jetzt notieren und den externen Beteiligten auf sicherem Weg mitteilen."
      );
    } catch (err) {
      setPwError(err?.message || "Das Passwort konnte nicht gesetzt werden.");
    } finally {
      setPwBusy(false);
    }
  };

  const handleRemovePassword = async () => {
    if (!canWrite || pwBusy) return;
    if (
      !window.confirm(
        "Zugangsschutz entfernen? Die externe Seite ist danach für alle erreichbar, die den Link kennen."
      )
    ) {
      return;
    }
    try {
      setPwBusy(true);
      setPwError(null);
      const state = await removeWebsitePassword(
        projectUuid,
        user?.email || user?.userDetails || ""
      );
      setData((prev) => ({ ...prev, password: state }));
      resetPwFelder();
      setInfo("Zugangsschutz entfernt.");
    } catch (err) {
      setPwError(err?.message || "Das Passwort konnte nicht entfernt werden.");
    } finally {
      setPwBusy(false);
    }
  };

  const aktiveBausteine = useMemo(
    () => WEBSITE_BLOCKS.filter((b) => data.blocks[b.key]).length,
    [data.blocks]
  );

  if (!projectUuid) return null;
  if (loading) {
    return <div className="project-website project-website--loading">Website wird geladen…</div>;
  }

  const blockProps = (key) => ({
    active: data.blocks[key] === true,
    disabled: !canWrite || data.blocks[key] !== true,
  });

  return (
    <div className="project-website">
      <div className="project-website__header">
        <div>
          <h3>Externe Projektwebsite</h3>
          <p className="project-website__subtitle">
            Inhalte für die externe Website von {project.code} {project.title}.
            {" "}
            {aktiveBausteine} von {WEBSITE_BLOCKS.length} Bausteinen aktiv.
          </p>
        </div>
        <div className="project-website__actions">
          {dirty && (
            <span className="project-website__dirty">Ungespeicherte Änderungen</span>
          )}
          {canWrite && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowMembers(true)}
            >
              Team zuordnen
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!canWrite || !dirty || saving}
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>

      {!canWrite && (
        <div className="project-website__hint">
          Nur Lesezugriff: Bearbeiten dürfen die GL, die Projektleitung und
          Team-Mitglieder, die diesem Projekt zugeteilt sind.
        </div>
      )}
      {error && (
        <div className="project-website__error" onClick={() => setError(null)}>
          {error}
        </div>
      )}
      {info && (
        <div className="project-website__info" onClick={() => setInfo(null)}>
          {info}
        </div>
      )}

      {/* Zugangsschutz gilt für die ganze Seite, nicht für einzelne Bausteine. */}
      <section className="project-website__block project-website__access">
        <div className="project-website__block-header">
          <h4>Zugangsschutz</h4>
          {data.password.gesetzt ? (
            <span className="project-website__badge project-website__badge--ok">
              Passwort gesetzt
            </span>
          ) : (
            <span className="project-website__badge project-website__badge--warn">
              Offen
            </span>
          )}
        </div>

        <p className="project-website__access-state">
          {data.password.gesetzt ? (
            <>
              Externe müssen dieses Passwort eingeben, um die Seite zu sehen.
              {data.password.gesetztAm && (
                <> Gesetzt am {formatDateTime(data.password.gesetztAm)}</>
              )}
              {data.password.gesetztVon && <> durch {data.password.gesetztVon}</>}.
              Das Passwort wird nur verschlüsselt gespeichert und kann nicht mehr
              angezeigt werden — nur ersetzen.
            </>
          ) : (
            <>
              Kein Passwort gesetzt: die externe Seite ist für alle erreichbar, die
              den Link kennen. Solange kein Zugangsschutz besteht, sollten dort keine
              Kontaktdaten aus der Adressliste veröffentlicht werden.
            </>
          )}
        </p>

        {canWrite && (
          <>
            <div className="project-website__fields">
              <div className="project-website__field">
                <label htmlFor="website-pw-neu">
                  {data.password.gesetzt ? "Neues Passwort" : "Passwort"}
                </label>
                <input
                  id="website-pw-neu"
                  type={pwSichtbar ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwNeu}
                  disabled={pwBusy}
                  onChange={(e) => {
                    setPwNeu(e.target.value);
                    setPwError(null);
                  }}
                />
              </div>
              <div className="project-website__field">
                <label htmlFor="website-pw-wdh">Wiederholen</label>
                <input
                  id="website-pw-wdh"
                  type={pwSichtbar ? "text" : "password"}
                  autoComplete="new-password"
                  value={pwWdh}
                  disabled={pwBusy}
                  onChange={(e) => {
                    setPwWdh(e.target.value);
                    setPwError(null);
                  }}
                />
              </div>
            </div>

            {pwError && (
              <div className="project-website__error" onClick={() => setPwError(null)}>
                {pwError}
              </div>
            )}

            <div className="project-website__access-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSetPassword}
                disabled={pwBusy || !pwNeu || !pwWdh}
              >
                {pwBusy
                  ? "Wird gespeichert…"
                  : data.password.gesetzt
                  ? "Passwort ersetzen"
                  : "Passwort setzen"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSuggestPassword}
                disabled={pwBusy}
              >
                Vorschlag erzeugen
              </button>
              <label className="project-website__reveal">
                <input
                  type="checkbox"
                  checked={pwSichtbar}
                  onChange={(e) => setPwSichtbar(e.target.checked)}
                />
                <span>Anzeigen</span>
              </label>
              {data.password.gesetzt && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleRemovePassword}
                  disabled={pwBusy}
                >
                  Zugangsschutz entfernen
                </button>
              )}
            </div>

            <p className="project-website__access-hint">
              Ein gemeinsames Passwort für alle externen Beteiligten. Mindestens{" "}
              {PASSWORD_MIN_LENGTH} Zeichen; „Vorschlag erzeugen" liefert ein starkes.
              Kein Passwort verwenden, das bei Malheur&amp;Fortuna anderswo im Einsatz
              ist. Setzen und Entfernen wirken sofort, unabhängig vom Speichern der
              Bausteine.
            </p>
          </>
        )}
      </section>

      {WEBSITE_BLOCKS.map((block) => {
        const { active, disabled } = blockProps(block.key);
        return (
          <section
            key={block.key}
            className={`project-website__block${
              active ? "" : " project-website__block--inactive"
            }`}
          >
            <div className="project-website__block-header">
              <label className="project-website__toggle">
                <input
                  type="checkbox"
                  checked={active}
                  disabled={!canWrite}
                  onChange={() => toggleBlock(block.key)}
                />
                <span>{block.label}</span>
              </label>
              {block.placeholder && (
                <span className="project-website__badge">Platzhalter</span>
              )}
              {!active && (
                <span className="project-website__state">nicht aktiviert</span>
              )}
            </div>

            <div className="project-website__block-body">
              {block.key === "planstand" && (
                <div className="project-website__fields">
                  <div className="project-website__field project-website__field--wide">
                    <label htmlFor="planstand-beschreibung">Beschreibung</label>
                    <textarea
                      id="planstand-beschreibung"
                      rows={3}
                      value={data.planstand.beschreibung}
                      disabled={disabled}
                      onChange={(e) =>
                        setPlanstandField("beschreibung", e.target.value)
                      }
                    />
                  </div>
                  <div className="project-website__field">
                    <label htmlFor="planstand-datum">Datum</label>
                    <input
                      id="planstand-datum"
                      type="date"
                      value={data.planstand.datum}
                      disabled={disabled}
                      onChange={(e) => setPlanstandField("datum", e.target.value)}
                    />
                  </div>
                  <div className="project-website__field project-website__field--wide">
                    <label htmlFor="planstand-link">Link auf Projektablage</label>
                    <input
                      id="planstand-link"
                      type="url"
                      placeholder="https://…"
                      value={data.planstand.ablageLink}
                      disabled={disabled}
                      onChange={(e) =>
                        setPlanstandField("ablageLink", e.target.value)
                      }
                    />
                  </div>
                </div>
              )}

              {block.key === "planstand" && (
                <ProjectWebsitePlanstand
                  plaene={data.plaene}
                  projectCode={projectCode}
                  disabled={disabled}
                  onCreate={handlePlanCreate}
                  onAddIndex={handlePlanAddIndex}
                  onUpdateMeta={handlePlanUpdateMeta}
                  onSetCurrent={handlePlanSetCurrent}
                  onDelete={handlePlanDelete}
                  onReorder={handlePlanReorder}
                />
              )}

              {block.key === "adressliste" && (
                <div className="project-website__addresses">
                  <div className="project-website__table-wrap">
                    <table className="project-website__table">
                      <thead>
                        <tr>
                          {ADRESS_FELDER.map((f) => (
                            <th key={f.key}>{f.label}</th>
                          ))}
                          <th aria-label="Aktionen" />
                        </tr>
                      </thead>
                      <tbody>
                        {data.adressen.length === 0 && (
                          <tr>
                            <td
                              className="project-website__empty"
                              colSpan={ADRESS_FELDER.length + 1}
                            >
                              Noch keine Einträge.
                            </td>
                          </tr>
                        )}
                        {data.adressen.map((a) => (
                          <tr key={a.id}>
                            {ADRESS_FELDER.map((f) => (
                              <td key={f.key}>
                                <input
                                  type={f.type || "text"}
                                  value={a[f.key] || ""}
                                  disabled={disabled}
                                  aria-label={f.label}
                                  onChange={(e) =>
                                    setAdressFeld(a.id, f.key, e.target.value)
                                  }
                                />
                              </td>
                            ))}
                            <td>
                              <button
                                type="button"
                                className="project-website__row-delete"
                                disabled={disabled}
                                onClick={() => removeAdresse(a.id)}
                                aria-label="Zeile entfernen"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={disabled}
                    onClick={addAdresse}
                  >
                    Zeile hinzufügen
                  </button>
                </div>
              )}

              {block.placeholder && (
                <p className="project-website__placeholder">
                  Platzhalter: Dieser Baustein kann bereits aktiviert werden, Inhalte
                  sind noch nicht erfassbar. Die externe Website zeigt den Abschnitt
                  erst, wenn er befüllbar ist.
                </p>
              )}
            </div>
          </section>
        );
      })}

      {showMembers && (
        <PendenzMembersModal
          projectCode={projectCode}
          projectTitle={project.title}
          onClose={() => setShowMembers(false)}
          onChanged={() => {
            loadAssignments();
            setShowMembers(false);
          }}
        />
      )}
    </div>
  );
};

export default ProjectWebsite;
